import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { uploadAdminFile } from "../../app/files";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { pathForAdminEntityScreen } from "../../app/routes";
import type { Schemas } from "../workspace/api.generated";
import { ContentView } from "./components/ContentView";
import { useContentMutation } from "./queries";
import type { ContentMutation } from "./queries";
import { parseContentType } from "./schema";

export function ContentCreatePage() {
  const services = useAdminApplication();
  const session = useAdminSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const type = parseContentType(searchParams.get("type"));
  const mutation = useContentMutation();
  const navigation = useAdminNavigation({
    contextKey: "contents",
    defaultListPath: `/contents?type=${type}`,
    originLabel: "콘텐츠 작성",
  });
  const fileRequests = useRef(new Set<AbortController>());

  useEffect(() => {
    const canonical = new URLSearchParams({ type });
    if (canonical.toString() !== searchParams.toString()) setSearchParams(canonical, { replace: true, state: location.state });
  }, [location.state, searchParams, setSearchParams, type]);

  useEffect(() => () => {
    for (const controller of fileRequests.current) controller.abort();
    fileRequests.current.clear();
  }, []);

  const upload = async (file: File) => {
    const controller = new AbortController();
    fileRequests.current.add(controller);
    try { return await uploadAdminFile(services, file, "CONTENT", controller.signal); }
    finally { fileRequests.current.delete(controller); }
  };

  return <ContentView
    mode="create"
    type={type}
    status=""
    canManage={session.authSession?.authorities?.includes("CONTENT_MANAGE") ?? false}
    backHref={navigation.backPath}
    backLabel={navigation.backLabel}
    onBack={navigation.backToList}
    onCancelEdit={navigation.backToList}
    onEditorTypeChange={(nextType) => setSearchParams(new URLSearchParams({ type: nextType }), { replace: true, state: location.state })}
    onTypeChange={() => undefined}
    onStatusChange={() => undefined}
    onSelect={() => undefined}
    onRetryList={() => undefined}
    onRetryDetail={() => undefined}
    onMutation={(input) => mutation.mutateAsync(input)}
    onMutationComplete={async (input: ContentMutation, result: unknown) => {
      if (input.kind !== "create") return;
      const saved = result as Schemas["ContentPostView"];
      navigate(pathForAdminEntityScreen("27", saved.id), { replace: true, state: location.state });
    }}
    onUpload={upload}
    onDownload={async () => undefined}
  />;
}
